import React from 'react';
import { useMutation } from '@apollo/client';
import { Button } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
// Api
import { SUBMIT_DELIVERABLES_FOR_REVIEW_V2 } from 'api/campaignV2/mutations';
import { GET_CAMPAIGN_OFFERS } from 'api/campaignV2/queries';
// Types
import { CBDeliverableItemType } from 'api/graphql-global-types';
import {
  SubmitDeliverablesForReviewV2,
  SubmitDeliverablesForReviewV2Variables,
} from 'api/campaignV2/types/SubmitDeliverablesForReviewV2';
// Helpers
import {
  AppearanceConfirmationItem,
  isAppearanceConfirmationSubmitted,
  isAppearanceDatePassed,
} from 'helpers/deliverables';
// UI
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './MarkAppearanceCompleteButton.module.scss';

type MarkAppearanceCompleteButtonProps = {
  offerId: string;
  campaignId: string;
  appearanceDate?: string | null;
  confirmationItem: AppearanceConfirmationItem | null;
  refetch?: () => void;
};

const MarkAppearanceCompleteButton: React.FC<
  MarkAppearanceCompleteButtonProps
> = ({ offerId, campaignId, appearanceDate, confirmationItem, refetch }) => {
  const [submitDeliverablesForReviewV2, { loading }] = useMutation<
    SubmitDeliverablesForReviewV2,
    SubmitDeliverablesForReviewV2Variables
  >(SUBMIT_DELIVERABLES_FOR_REVIEW_V2);

  const alreadySubmitted = isAppearanceConfirmationSubmitted(confirmationItem);

  // The action only becomes available once the scheduled appearance moment has
  // passed (talent must have actually appeared). Hidden entirely beforehand.
  if (!alreadySubmitted && !isAppearanceDatePassed(appearanceDate)) {
    return null;
  }

  if (alreadySubmitted) {
    return (
      <div className={styles.completedNote}>
        <CheckCircleOutlined className={styles.completedIcon} />
        <span>Marked as complete</span>
      </div>
    );
  }

  if (!confirmationItem) {
    return null;
  }

  const handleMarkComplete = async () => {
    try {
      await submitDeliverablesForReviewV2({
        variables: {
          input: {
            offerId,
            items: [
              {
                deliverableItemId: confirmationItem.deliverableItemId,
                type: CBDeliverableItemType.CONFIRMATION,
              },
            ],
          },
        },
        refetchQueries: [
          {
            query: GET_CAMPAIGN_OFFERS,
            variables: { input: { campaignId } },
          },
        ],
        awaitRefetchQueries: true,
      });

      successNotification('Appearance marked as complete.');
      refetch?.();
    } catch (error) {
      errorNotification(
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <Button
      type="primary"
      block
      loading={loading}
      onClick={handleMarkComplete}
      className={styles.markCompleteButton}
    >
      Mark as Complete
    </Button>
  );
};

export default MarkAppearanceCompleteButton;
